/**
 * The zoom range of the overview map, as the instance configures it.
 *
 * The backend publishes it in the settings of `/api/features`. Until it does, and on any instance
 * whose backend is older, the map falls back to the `mapZoom` toggle and then to its constants.
 */
export interface IZoomRange {
  min: number;
  max: number;
  default: number;
}

/**
 * The shape of the `mapZoom` toggle, which stays as a fallback while the instances migrate.
 */
export interface IZoomRangeToggle {
  min?: unknown;
  max?: unknown;
  default?: unknown;
}

// The levels the backend validates against. Leaflet itself counts from 0.
const LOWEST_LEVEL = 1
const HIGHEST_LEVEL = 21

/**
 * One level, or nothing.
 *
 * A whole number between 1 and 21. Anything else — missing, null, a string, a fraction, out of
 * range — is not a level. The backend checks the same, but an older one does not, and a value
 * written into the instance configuration by hand never passes through that check at all.
 */
const level = (raw: unknown): number | undefined => {
  if (typeof raw !== 'number' || !Number.isInteger(raw)) {
    return undefined
  }
  return raw >= LOWEST_LEVEL && raw <= HIGHEST_LEVEL ? raw : undefined
}

/**
 * The range an instance configured, or nothing at all.
 *
 * All three or none: a valid minimum next to a broken maximum would leave a map that cannot zoom
 * anywhere, which is worse than the defaults. The order has to hold as well, or the same happens.
 *
 * Returning nothing rather than the defaults is deliberate — it lets the caller tell "this
 * source says nothing" from "this source says this", which is what the fallback from the
 * settings to the toggle and on to the constants is built out of.
 */
const rangeOf = (
  rawMin: unknown,
  rawMax: unknown,
  rawStart: unknown
): IZoomRange | undefined => {
  const min = level(rawMin)
  const max = level(rawMax)
  const start = level(rawStart)

  if (min === undefined || max === undefined || start === undefined) {
    return undefined
  }
  if (min > start || start > max) {
    return undefined
  }

  return { min, max, default: start }
}

/**
 * The range an instance publishes in the settings of `/api/features`.
 *
 * Takes `unknown` on purpose. Whether these three keys exist at all depends on the version of
 * `@lopo-tech/frontends-shared` a build pins, and the white-label webs pin releases before 1.30.0,
 * which do not know them. Measured against such a build: a parameter declared as three optional
 * properties fails with TS2559, because it shares no property with the settings it is handed.
 * So the shape is checked here, per value, instead of being promised by the signature.
 */
export const zoomRangeOf = (settings?: unknown): IZoomRange | undefined => {
  const values = (settings ?? {}) as Record<string, unknown>
  return rangeOf(
    values.overviewMapMinZoom,
    values.overviewMapMaxZoom,
    values.overviewMapDefaultZoom
  )
}

/**
 * The same reading of the `mapZoom` toggle, which an instance may still carry.
 *
 * All or nothing holds per source, not across them: an incomplete set of settings falls back to
 * the toggle, and only a toggle that is unusable too falls through to the constants.
 */
export const zoomRangeOfToggle = (toggle?: IZoomRangeToggle): IZoomRange | undefined =>
  rangeOf(toggle?.min, toggle?.max, toggle?.default)
